import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeftIcon, Loader2 } from "lucide-react";
import { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import DayPicker from "~/components/DayPicker";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  ChronologyFormValues,
  EMPTY_CHRONOLOGY_FORM_VALUES,
} from "../helpers/chronologyRequest";
import { createChronologyEventSchema } from "../validations/chronologyEvent";

export enum EditorStep {
  EVENT_DETAILS = 1,
  SOURCE_DETAILS = 2,
}

interface ChronologyEventEditorProps {
  initialValues?: ChronologyFormValues;
  isSaving: boolean;
  isLoadingSuggestion?: boolean;
  submitLabelId: string;
  onSubmit: (values: ChronologyFormValues) => void;
  onCancel: () => void;
  step: EditorStep;
  onStepChange: (step: EditorStep) => void;
}

export const ChronologyEventEditor: FC<ChronologyEventEditorProps> = ({
  initialValues,
  isSaving,
  isLoadingSuggestion,
  submitLabelId,
  onSubmit,
  onCancel,
  step,
  onStepChange,
}) => {
  const intl = useIntl();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChronologyFormValues>({
    resolver: zodResolver(createChronologyEventSchema(intl)),
    defaultValues: initialValues ?? EMPTY_CHRONOLOGY_FORM_VALUES,
  });

  useEffect(() => {
    if (!initialValues) return;
    reset(initialValues, { keepDirtyValues: true });
  }, [initialValues, reset]);

  const STEP1_FIELDS = [
    "detail",
    "eventDate",
    "eventType",
    "provider",
    "facility",
  ] as const;

  const handleFormSubmit = handleSubmit(
    (values) => {
      if (step === EditorStep.EVENT_DETAILS) {
        onStepChange(EditorStep.SOURCE_DETAILS);
      } else {
        onSubmit(values);
      }
    },
    (fieldErrors) => {
      if (step === EditorStep.SOURCE_DETAILS) {
        const hasStep1Error = STEP1_FIELDS.some((f) => f in fieldErrors);
        if (hasStep1Error) onStepChange(EditorStep.EVENT_DETAILS);
      }
    }
  );

  const handleBack = () => {
    onStepChange(EditorStep.EVENT_DETAILS);
  };

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col gap-3">
      <div
        className={
          step !== EditorStep.EVENT_DETAILS ? "hidden" : "flex flex-col gap-3"
        }
        aria-hidden={step !== EditorStep.EVENT_DETAILS || undefined}
      >
        {isLoadingSuggestion && (
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <FormattedMessage
              id="chronology.ai.loading"
              defaultMessage="Pre-filling fields with AI…"
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-type">
            <FormattedMessage
              id="chronology.field.eventType"
              defaultMessage="Event Type"
            />
          </Label>
          <Input id="chronology-event-type" {...register("eventType")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-detail">
            <FormattedMessage
              id="chronology.field.detail"
              defaultMessage="Detail"
            />
          </Label>
          <Textarea
            id="chronology-event-detail"
            rows={3}
            placeholder={intl.formatMessage({
              id: "chronology.field.detailPlaceholder",
            })}
            aria-invalid={Boolean(errors.detail)}
            {...register("detail")}
          />
          {errors.detail && (
            <p className="text-xs text-destructive">{errors.detail.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-date">
            <FormattedMessage
              id="chronology.field.date"
              defaultMessage="Date"
            />
          </Label>
          <Controller
            control={control}
            name="eventDate"
            render={({ field }) => (
              <DayPicker
                value={field.value}
                onChange={field.onChange}
                maxDate={new Date()}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-provider">
            <FormattedMessage
              id="chronology.field.provider"
              defaultMessage="Provider"
            />
          </Label>
          <Input id="chronology-event-provider" {...register("provider")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-facility">
            <FormattedMessage
              id="chronology.field.facility"
              defaultMessage="Facility"
            />
          </Label>
          <Input id="chronology-event-facility" {...register("facility")} />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSaving}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" size="sm">
            <FormattedMessage
              id="chronology.action.continue"
              defaultMessage="Continue"
            />
          </Button>
        </div>
      </div>

      <div
        className={
          step !== EditorStep.SOURCE_DETAILS ? "hidden" : "flex flex-col gap-3"
        }
        aria-hidden={step !== EditorStep.SOURCE_DETAILS || undefined}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-category">
            <FormattedMessage
              id="chronology.field.documentCategory"
              defaultMessage="Document Category"
            />
          </Label>
          <Input
            id="chronology-event-category"
            {...register("documentCategory")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chronology-event-specialty">
            <FormattedMessage
              id="chronology.field.providerSpecialty"
              defaultMessage="Provider Specialty"
            />
          </Label>
          <Input
            id="chronology-event-specialty"
            {...register("providerSpecialty")}
          />
        </div>

        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="chronology-event-page">
              <FormattedMessage
                id="chronology.field.sourcePage"
                defaultMessage="Source Page"
              />
            </Label>
            <Input
              id="chronology-event-page"
              inputMode="numeric"
              aria-invalid={Boolean(errors.sourcePage)}
              {...register("sourcePage")}
            />
            {errors.sourcePage && (
              <p className="text-xs text-destructive">
                {errors.sourcePage.message}
              </p>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="chronology-event-bates">
              <FormattedMessage
                id="chronology.field.referenceNumber"
                defaultMessage="Reference Number"
              />
            </Label>
            <Input id="chronology-event-bates" {...register("batesNumber")} />
          </div>
        </div>

        <div className="flex justify-between pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBack}
            disabled={isSaving}
          >
            <ChevronLeftIcon className="mr-1 h-4 w-4" />
            <FormattedMessage
              id="chronology.action.back"
              defaultMessage="Back"
            />
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isSaving}
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button type="submit" size="sm" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              <FormattedMessage id={submitLabelId} />
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
